import { useRef, type CSSProperties, type ReactNode } from 'react';
import { C, F, APPROVAL_STATUS_CFG, BOOKING_STATUS_CFG } from './theme';
import type { Base } from '../../lib/opsBoardTypes';

export function Pill({ label, color, bg }: { label: string; color: string; bg?: string }) {
  return (
    <span
      style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
        color, background: bg ?? color + '22', border: `1px solid ${color}44`,
        borderRadius: 4, padding: '1px 6px', fontFamily: F, whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

export function BaseBadge({ base }: { base?: Base }) {
  if (!base) return null;
  return (
    <span
      style={{
        fontSize: 9, fontWeight: 700, color: '#fff',
        background: base.ui_color, borderRadius: 3, padding: '1px 5px', fontFamily: F,
      }}
    >
      {base.short_code}
    </span>
  );
}

export function ApprovalChip({ status }: { status: string }) {
  const cfg = APPROVAL_STATUS_CFG[status] ?? { color: C.textMuted, bg: C.pill, label: status, icon: '' };
  return <Pill label={`${cfg.icon} ${cfg.label}`.trim()} color={cfg.color} bg={cfg.bg} />;
}

export function StatusPill({ status }: { status: string }) {
  const cfg = BOOKING_STATUS_CFG[status] ?? { color: C.textMuted, bg: C.pill, label: status, icon: '' };
  return <Pill label={`${cfg.icon} ${cfg.label}`.trim()} color={cfg.color} bg={cfg.bg} />;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.textMuted, fontFamily: F, fontSize: 13, padding: 24 }}>
      <div
        style={{
          width: 18, height: 18, border: `3px solid ${C.border}`,
          borderTopColor: C.accent, borderRadius: '50%', animation: 'spin 0.7s linear infinite',
        }}
      />
      {label ?? 'Loading…'}
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      style={{
        background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 8,
        padding: '12px 16px', color: C.red, fontFamily: F, fontSize: 13,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}
    >
      <span>⚠ {message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            background: C.surface, border: `1px solid ${C.redBorder}`, color: C.red,
            borderRadius: 5, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F,
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

// Advisory warnings banner — the bookings API returns these on create/edit. They
// are advisory, never blocking (§8.1). Dismissible.
export function WarningsBanner({ warnings, onDismiss }: { warnings: string[]; onDismiss: () => void }) {
  if (warnings.length === 0) return null;
  return (
    <div
      style={{
        background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 8,
        padding: '12px 16px', color: C.amber, fontFamily: F, fontSize: 12, marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <strong style={{ fontSize: 12, letterSpacing: '0.04em' }}>⚠ ADVISORY ({warnings.length})</strong>
        <button
          onClick={onDismiss}
          style={{ background: 'none', border: 'none', color: C.amber, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
          aria-label="Dismiss advisories"
        >
          ✕
        </button>
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
        {warnings.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

// Modal shell (centered overlay) reused by BookingForm.
export function ModalOverlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  // PR-000025: close only when the interaction BOTH starts and ends on the backdrop itself.
  // A bare onClick fires even when the mouse went DOWN inside the dialog (e.g. drag-selecting
  // text in a field) and came UP on the backdrop — that would close the form on a text-select
  // drag. Record where the press started; the click closes only if it also began on the backdrop.
  const downOnBackdrop = useRef(false);
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16,
      }}
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        const started = downOnBackdrop.current;
        downOnBackdrop.current = false;
        if (started && e.target === e.currentTarget) onClose();
      }}
    >
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

export const inputStyle: CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.surface,
  border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px',
  fontSize: 13, fontFamily: F, color: C.text, outline: 'none',
};

export function Field({ label, req, children }: { label: string; req?: boolean; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em',
          marginBottom: 3, textTransform: 'uppercase', fontFamily: F,
        }}
      >
        {label}
        {req && <span style={{ color: C.red, marginLeft: 2 }}>*</span>}
      </div>
      {children}
    </div>
  );
}
